/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Box,
  Container,
  CssBaseline,
  PaletteMode,
  Snackbar,
  ThemeProvider
} from "@mui/material";
import {
  MutableRefObject,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import ReactGA from "react-ga4";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { OrderDtoPaymentTypeEnum, OrderItemDto, Stock, UserDto } from "./api";
import { bootstrapAuth, setOnUnauthorized } from "./auth/authService";
import Footer from "./components/layout/Footer";
import Header from "./components/layout/Header";
import GoogleAnalytics from "./components/utils/GoogleAnalytics";
import GoogleRecaptcha from "./components/utils/GoogleRecaptcha";
import LoadingSpinner from "./components/utils/LoadingSpinner";
import ScrollPositionRestore from "./components/utils/ScrollPositionRestore";
import { BannerModal } from "./components/view/BannerModal";
import { getConfig } from "./config";
import {
  CART,
  COLOR_MODE,
  LANGUAGE,
  NECESSARY_COOKIES,
  NOTE,
  OPTIONAL_COOKIES,
  PAYMENT_TYPE
} from "./constants/global";
import { routes } from "./constants/routes";
import { ApiContext } from "./context/ApiContext";
import { CartContext, CartContextSchema } from "./context/CartContext";
import { ColorContext } from "./context/ColorContext";
import { ErrorContext, ErrorContextSchema } from "./context/ErrorContext";
import {
  GoogleServicesContext,
  GoogleServicesContextSchema
} from "./context/GoogleServicesContext";
import { LanguageContext, LanguageContextSchema, LanguageMeta } from "./context/LanguageContext";
import { ProductsContext, ProductsContextSchema } from "./context/ProductsContext";
import { UserContext, UserContextSchema } from "./context/UserContext";
import i18n from "./i18n";
import languages from "./locales/languages.json";
import HomePage from "./pages/HomePage";
import { authApi, certificateApi, contactApi, orderApi, stockApi } from "./querries/api";
import { darkTheme } from "./theme/dark";
import { lightTheme } from "./theme/light";
import { getItem, setItem } from "./utils/storage";

const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const PasswordChangePage = lazy(() => import("./pages/auth/PasswordChangePage"));
const PasswordResetPage = lazy(() => import("./pages/auth/PasswordResetPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PartnerPage = lazy(() => import("./pages/PartnerPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const CertificatesPage = lazy(() => import("./pages/CertificatesPage"));

const App = () => {
  const { t } = useTranslation();

  // APIs
  const [user, setUser] = useState<UserDto | undefined>();

  const apiSchemas = useMemo(
    () => ({
      authApi,
      orderApi,
      contactApi,
      stockApi,
      certificateApi
    }),
    []
  );

  // Theme

  const storedMode = getItem(COLOR_MODE) as PaletteMode;
  const [colorMode, setColorMode] = useState<PaletteMode>(storedMode || ("light" as PaletteMode));
  const theme = useMemo(() => (colorMode === "light" ? lightTheme : darkTheme), [colorMode]);

  const selectedColorMode = useMemo(
    () => ({
      selectedMode: colorMode,
      toggleColorMode: (mode: PaletteMode) => setColorMode(mode)
    }),
    []
  );

  useEffect(() => {
    setItem(COLOR_MODE, colorMode);
  }, [colorMode]);

  // Language

  const storedLanguage = getItem(LANGUAGE) ?? languages.find((l) => l.default)?.code ?? "sk";
  const [language, setLanguageState] = useState<string>(storedLanguage);

  const languageContextData = useMemo(
    () =>
      ({
        selectedLanguage: language,
        availableLanguages: languages as LanguageMeta[],
        setLanguage: (lang: string) => {
          setLanguageState(lang);
        }
      } as LanguageContextSchema),
    [language]
  );

  useEffect(() => {
    document.documentElement.lang = language;
    setItem(LANGUAGE, language);
    i18n.changeLanguage(language);
  }, [language]);

  // Errors

  const [error, setError] = useState<string | null>();

  const errorContextData = useMemo(
    () =>
      ({
        error,
        setError
      } as ErrorContextSchema),
    [error]
  );

  // User

  const [authResolved, setAuthResolved] = useState<boolean>(false);

  useEffect(() => {
    setOnUnauthorized(() => setUser(undefined));

    bootstrapAuth()
      .then((result) => {
        if (result) {
          setUser(result.user);
        } else {
          setUser(undefined);
        }
      })
      .catch(() => {
        setUser(undefined);
      })
      .finally(() => {
        setAuthResolved(true);
      });
  }, []);

  const userContextData = useMemo(
    () =>
      ({
        user,
        setUser
      } as UserContextSchema),
    [user]
  );

  // Cart

  const [cart, setCart] = useState<OrderItemDto[]>();
  const [paymentType, setPaymentType] = useState<OrderDtoPaymentTypeEnum>();
  const [note, setNote] = useState<string>();

  const addItem = (item: OrderItemDto) => {
    setCart((prevCart) => {
      if (!prevCart) return [item];

      const itemToUpdateIndex = prevCart.map((i) => i.ean).indexOf(item.ean);

      if (itemToUpdateIndex === -1) {
        return [...prevCart, item];
      }

      if (item.quantity === undefined || item.quantity === null) return prevCart;

      const itemToUpdate = prevCart[itemToUpdateIndex];
      const newCart = [...prevCart];
      newCart[itemToUpdateIndex] = {
        ...itemToUpdate,
        quantity: itemToUpdate.quantity ?? 0 + item.quantity
      };

      return newCart;
    });
  };

  const updateItem = (item: OrderItemDto) =>
    setCart((prevCart) => {
      if (!prevCart) return [item];

      const itemToUpdateIndex = prevCart.map((i) => i.ean).indexOf(item.ean);

      if (itemToUpdateIndex === -1) {
        return [...prevCart, item];
      }

      const itemToUpdate = prevCart[itemToUpdateIndex];
      const newCart = [...prevCart];
      newCart[itemToUpdateIndex] = {
        ...itemToUpdate,
        ...item
      };

      return newCart;
    });

  const removeItem = (ean: string) =>
    setCart((prevCart) => {
      if (!prevCart) return [];

      return prevCart.filter((item) => item.ean !== ean);
    });

  const resetCartSoft = () => {
    setCart(undefined);
    setPaymentType(undefined);
    setNote(undefined);
  };

  const resetCartHard = () => {
    setItem(CART, undefined);
    setItem(PAYMENT_TYPE, undefined);
    setItem(NOTE, undefined);
    resetCartSoft();
  };

  const refreshCart = () => {
    if (user) {
      setCart(getItem(CART) ?? undefined);
      setPaymentType(getItem(PAYMENT_TYPE) as OrderDtoPaymentTypeEnum);
      setNote(getItem(NOTE));
    } else {
      resetCartSoft();
    }
  };

  const cartContextData = useMemo(
    () =>
      ({
        cart,
        paymentType,
        note,
        setPaymentType,
        setNote,
        addItem,
        removeItem,
        updateItem,
        resetCartSoft,
        resetCartHard,
        refreshCart
      } as CartContextSchema),
    [cart]
  );

  useEffect(() => {
    if (user) {
      if (cart) {
        setItem(CART, cart);
      }

      if (paymentType) {
        setItem(PAYMENT_TYPE, paymentType);
      }

      if (note) {
        setItem(NOTE, note);
      }
    }
  }, [cart, paymentType, note, user]);

  useEffect(() => {
    refreshCart();
  }, [user]);

  // Google services

  const ref = useRef<ReCAPTCHA | null>(null);
  const [neccessaryCookiesAllowed, setNeccessaryCookiesAllowed] = useState<boolean>(
    getItem(NECESSARY_COOKIES)
  );
  const [optionalCookiesAllowed, setOptionalCookiesAllowed] = useState<boolean>(
    getItem(OPTIONAL_COOKIES)
  );
  const [recaptchaRef, setRecaptchaRef] = useState<MutableRefObject<ReCAPTCHA | null> | null>(null);

  const [analyticsInitialized, setAnalyticsInitialized] = useState<boolean>(false);

  const analyticsSend = useCallback(
    (page: string, title?: string) => {
      if (optionalCookiesAllowed && analyticsInitialized) {
        ReactGA.send({ hitType: "pageview", page, title });
      }
    },
    [optionalCookiesAllowed, analyticsInitialized]
  );

  const analyticsEvent = useCallback(
    (category: string, action: string, label?: string, value?: number) => {
      if (optionalCookiesAllowed && analyticsInitialized) {
        ReactGA.event({
          category,
          action,
          label,
          value
        });
      }
    },
    [optionalCookiesAllowed, analyticsInitialized]
  );

  const googleServicesContextData = useMemo(
    () =>
      ({
        recaptchaRef,
        neccessaryCookiesAllowed,
        optionalCookiesAllowed,
        setNeccessaryCookiesAllowed,
        setOptionalCookiesAllowed,
        analyticsSend,
        analyticsEvent
      } as GoogleServicesContextSchema),
    [recaptchaRef, neccessaryCookiesAllowed, optionalCookiesAllowed, analyticsSend, analyticsEvent]
  );

  useEffect(() => {
    if (ref) {
      setRecaptchaRef(ref);
    }
  }, [ref]);

  const GA_MEASUREMENT_ID = getConfig().GA_MEASUREMENT_ID;

  useEffect(() => {
    if (!analyticsInitialized && optionalCookiesAllowed && GA_MEASUREMENT_ID) {
      ReactGA.initialize(GA_MEASUREMENT_ID);
      setAnalyticsInitialized(true);
    }
  }, [GA_MEASUREMENT_ID, optionalCookiesAllowed]);

  // Products

  const [products, setProducts] = useState<Stock[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const inFlightProducts = useRef<Promise<void> | null>(null);

  const loadProducts = useCallback(() => {
    // Dedupe overlapping loads (e.g. the initial App-level fetch and the
    // ProductsPage visit fetch firing together) into a single request.
    if (inFlightProducts.current) return inFlightProducts.current;

    setLoading(true);

    const request = apiSchemas?.stockApi
      .getAllStock()
      .then((response) => {
        setProducts(response);
        setLoading(false);
      })
      .catch((error) => {
        error.response?.json().then((json: unknown) => {
          console.error(json);

          setLoading(false);
          setError(error);
        });
      })
      .finally(() => {
        inFlightProducts.current = null;
      });

    inFlightProducts.current = request ?? null;
    return request;
  }, []);

  useEffect(() => {
    if (!authResolved) return;

    loadProducts();
  }, [loadProducts, user, authResolved]);

  const productContextData = useMemo(
    () =>
      ({
        loading,
        products,
        reloadProducts: loadProducts
      } as ProductsContextSchema),
    [loading, products, loadProducts]
  );

  return (
    <ApiContext.Provider value={apiSchemas}>
      <ColorContext.Provider value={selectedColorMode}>
        <LanguageContext.Provider value={languageContextData}>
          <ProductsContext.Provider value={productContextData}>
            <CartContext.Provider value={cartContextData}>
              <ErrorContext.Provider value={errorContextData}>
                <UserContext.Provider value={userContextData}>
                  <ThemeProvider theme={theme}>
                    <GoogleServicesContext.Provider value={googleServicesContextData}>
                      <CssBaseline enableColorScheme />
                      <Router>
                        <Container
                          maxWidth="xl"
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            minHeight: "100%",
                            px: { xs: 4, md: 6 },
                            overflowX: "hidden"
                          }}>
                          <a href="#main-content" className="skip-link">
                            {t("buttons.skipToContent")}
                          </a>
                          <Header />
                          <Box
                            component="main"
                            id="main-content"
                            sx={{
                              flex: "1 0 auto",
                              flexDirection: "column",
                              display: "flex",
                              pt: { xs: 17, lg: 30 }
                            }}>
                            <ScrollPositionRestore />
                            <GoogleAnalytics />
                            <Snackbar
                              open={!!error}
                              anchorOrigin={{ vertical: "top", horizontal: "center" }}
                              autoHideDuration={6000}
                              onClose={() => setError(null)}>
                              <Alert
                                onClose={() => setError(null)}
                                severity="error"
                                sx={{
                                  display: "flex",
                                  alignItems: "center"
                                }}>
                                {t("errorMessage")}
                              </Alert>
                            </Snackbar>
                            <Suspense
                              fallback={
                                <Box
                                  sx={{
                                    width: 1,
                                    height: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                  }}>
                                  <LoadingSpinner />
                                </Box>
                              }>
                              <Routes>
                                {/* Auth */}
                                <Route path={routes.login} element={<LoginPage />}></Route>
                                <Route
                                  path={routes.passwordChange}
                                  element={<PasswordChangePage />}></Route>
                                <Route
                                  path={routes.passwordReset}
                                  element={<PasswordResetPage />}></Route>
                                {/* Products */}
                                <Route path={routes.home} element={<HomePage />}></Route>
                                <Route path={routes.products} element={<ProductsPage />}></Route>
                                <Route
                                  path={`${routes.products}/${routes.ean}`}
                                  element={<ProductPage />}></Route>
                                <Route
                                  path={routes.certificates}
                                  element={<CertificatesPage />}></Route>
                                <Route
                                  path={`${routes.certificates}/${routes.certificateId}`}
                                  element={<CertificatesPage />}></Route>
                                <Route
                                  path={`${routes.certificates}/${routes.certificateId}/:language`}
                                  element={<CertificatesPage />}></Route>
                                {/* Contact and partner */}
                                <Route path={routes.contact} element={<ContactPage />}></Route>
                                <Route path={routes.partner} element={<PartnerPage />}></Route>
                                {/* Cart */}
                                <Route path={routes.cart} element={<CartPage />}></Route>
                                {/* Error handling */}
                                <Route path="*" element={<Navigate to={routes.home} replace />} />
                              </Routes>
                            </Suspense>
                          </Box>
                          <Footer />
                          <BannerModal />
                        </Container>
                      </Router>
                      <GoogleRecaptcha recaptchaRef={ref} />
                    </GoogleServicesContext.Provider>
                  </ThemeProvider>
                </UserContext.Provider>
              </ErrorContext.Provider>
            </CartContext.Provider>
          </ProductsContext.Provider>
        </LanguageContext.Provider>
      </ColorContext.Provider>
    </ApiContext.Provider>
  );
};

export default App;
